import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Select,
  Input,
  DataTable,
  Badge,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { usePlatformUsers, PlatformUserStatusBadge } from '@/features/platform-users';
import type { PlatformUserListItem } from '@/types/platform-user';
import {
  PLATFORM_ROLE_BADGE_VARIANT,
  PLATFORM_ROLE_LABEL,
  type PlatformRole,
} from '@/types/platform-user';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';

// ─── Constants ──────────────────────────────────────────────────────────────

const ROLE_OPTIONS = [
  { value: '', label: 'All Roles' },
  { value: 'SUPER_ADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'MODERATOR', label: 'Moderator' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Deactivated' },
];

// ─── Icons ──────────────────────────────────────────────────────────────────

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}

function CrownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 16L3 7l5.5 4L12 4l3.5 7L21 7l-2 9H5zm0 4h14" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatRelativeTime(dateStr: string | undefined): string {
  if (!dateStr) return '\u2014';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'in the future';
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
}

// ─── KPI Card ───────────────────────────────────────────────────────────────

interface KpiConfig {
  title: string;
  value: string;
  icon: React.ReactNode;
  gradient: string;
  iconBg: string;
  tintBg: string;
  darkTintBg: string;
}

function KpiCard({ title, value, icon, gradient, iconBg, tintBg, darkTintBg }: KpiConfig) {
  return (
    <div
      className={`relative overflow-hidden rounded-xl ${tintBg} ${darkTintBg} shadow-md hover:shadow-lg transition-all duration-200 p-5 animate-slide-up`}
    >
      <div className={`absolute -top-6 -right-6 w-20 h-20 rounded-full ${gradient} opacity-20`} />
      <div className="relative flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center text-white shadow-sm`}>
          {icon}
        </div>
        <span className="text-sm font-medium text-neutral-500 dark:text-neutral-400">{title}</span>
      </div>
      <div className="relative">
        <p className="text-2xl font-bold text-neutral-900 dark:text-white tabular-nums">{value}</p>
      </div>
    </div>
  );
}

// ─── Content ────────────────────────────────────────────────────────────────

function PlatformUserListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isSuperAdmin = roleLevel >= ROLE_LEVELS.SUPER_ADMIN;

  const page = Number(searchParams.get('page') ?? '1');
  const search = searchParams.get('search') ?? '';
  const roleFilter = (searchParams.get('role') ?? '') as PlatformRole | '';
  const activeFilter = searchParams.get('is_active') ?? '';

  const [searchInput, setSearchInput] = useState(search);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set('search', value);
          else next.delete('search');
          next.set('page', '1');
          return next;
        });
      }, 300);
    },
    [setSearchParams],
  );

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) next.set(key, value);
      else next.delete(key);
      next.set('page', '1');
      return next;
    });
  }

  function setPage(p: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(p));
      return next;
    });
  }

  const { data, isLoading, error } = usePlatformUsers({
    page,
    limit: 20,
    search: search || undefined,
    role: roleFilter || undefined,
    is_active: activeFilter ? activeFilter === 'true' : undefined,
  });

  // KPI counts — read meta.total only (limit=1 keeps payload minimal).
  // For "Active Today" we still need a sample of recent logins, so we fetch up to 100;
  // if there are >100 active users, render "+" suffix to signal truncation.
  const { data: activeMeta } = usePlatformUsers({ page: 1, limit: 1, is_active: true });
  const { data: superAdminMeta } = usePlatformUsers({
    page: 1,
    limit: 1,
    role: 'SUPER_ADMIN',
    is_active: true,
  });
  const { data: activeSample } = usePlatformUsers({ page: 1, limit: 100, is_active: true });

  useEffect(() => {
    if (error) toast({ title: (error as Error).message, variant: 'error' });
  }, [error, toast]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        document.querySelector<HTMLInputElement>('input[data-role="platform-user-search"]')?.focus();
      }
      if (e.key === 'n' && isSuperAdmin) {
        e.preventDefault();
        navigate('/platform-users/new');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, isSuperAdmin]);

  const users = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const todayIso = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);

  const activeSampleRows = activeSample?.data ?? [];
  const activeTotal = activeMeta?.meta?.total ?? 0;
  const activeTodayInSample = activeSampleRows.filter(
    (u) => u.last_login_at && u.last_login_at >= todayIso,
  ).length;
  // If the sample is full AND the true total exceeds it, the count is a lower bound.
  const activeTodayTruncated = activeSampleRows.length >= 100 && activeTotal > 100;
  const activeTodayDisplay = `${activeTodayInSample.toLocaleString('en-IN')}${activeTodayTruncated ? '+' : ''}`;

  const columns: ColumnDef<PlatformUserListItem>[] = [
    {
      key: 'name',
      header: 'User',
      render: (_v, row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-indigo-600 text-xs font-semibold text-white shadow-sm">
            {initialsOf(row.name)}
          </div>
          <div className="min-w-0">
            <button
              type="button"
              onClick={() => navigate(`/platform-users/${row.id}`)}
              className="block text-sm font-semibold text-neutral-900 dark:text-white hover:text-brand-600 dark:hover:text-brand-400 text-left truncate"
            >
              {row.name}
            </button>
            <p className="mt-0.5 truncate font-mono text-xs text-neutral-500 dark:text-neutral-400">
              {row.email}
            </p>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      header: 'Role',
      render: (_v, row) => (
        <Badge variant={PLATFORM_ROLE_BADGE_VARIANT[row.role]} size="sm">
          <span className="flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {PLATFORM_ROLE_LABEL[row.role]}
          </span>
        </Badge>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (_v, row) => (
        <span className="font-mono text-sm tabular-nums text-neutral-600 dark:text-neutral-400">
          {row.phone || '\u2014'}
        </span>
      ),
    },
    {
      key: 'last_login_at',
      header: 'Last Login',
      render: (_v, row) => (
        <span className="font-mono text-sm tabular-nums text-neutral-600 dark:text-neutral-400">
          {formatRelativeTime(row.last_login_at)}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (_v, row) => <PlatformUserStatusBadge isActive={row.is_active} />,
    },
    {
      key: 'actions',
      header: '',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/platform-users/${row.id}`)}
          className="rounded-lg p-1.5 text-neutral-500 transition-colors hover:bg-brand-50 hover:text-brand-600 dark:hover:bg-brand-900/20"
          title="View"
          aria-label={`View ${row.name}`}
        >
          <EyeIcon className="h-4 w-4" />
        </button>
      ),
    },
  ];

  // ── Loading ───────────────────────────────────────────────────────────────

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="mb-6 rounded-2xl" />
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3 lg:gap-6">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={56} className="mb-4 rounded-xl" />
        <Skeleton variant="rect" height={400} className="rounded-xl" />
      </>
    );
  }

  const hasFilters = Boolean(search || roleFilter || activeFilter);

  // ── Empty (no users, no filters) ──────────────────────────────────────────

  if (!isLoading && total === 0 && !hasFilters) {
    return (
      <>
        <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 p-6 text-white shadow-[0_20px_60px_-20px_rgba(67,56,202,0.55)] animate-fade-in">
          <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
          <div className="relative flex items-center justify-between">
            <div>
              <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/20">
                <ShieldIcon className="h-3.5 w-3.5" />
                Platform Access
              </div>
              <h1 className="text-2xl font-bold text-white">Platform Users</h1>
              <p className="mt-1 text-indigo-100">
                Manage Super Admin, Admin, and Moderator accounts that operate across all tenants.
              </p>
            </div>
            {isSuperAdmin && (
              <Button
                variant="secondary"
                onClick={() => navigate('/platform-users/new')}
                className="border-white/30 bg-white/20 text-white hover:bg-white/30"
              >
                <PlusIcon className="mr-1.5 h-4 w-4" />
                Add Platform User
              </Button>
            )}
          </div>
        </div>

        <EmptyState
          variant="page"
          icon={<ShieldIcon className="h-full w-full" />}
          title="No platform users yet"
          description="Platform users are internal staff with cross-tenant access. Add your first platform user to delegate administration."
          action={
            isSuperAdmin
              ? { label: 'Add Platform User', onClick: () => navigate('/platform-users/new') }
              : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      {/* Gradient header */}
      <div className="relative mb-6 overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 via-indigo-600 to-violet-600 p-6 text-white shadow-[0_20px_60px_-20px_rgba(67,56,202,0.55)] animate-fade-in">
        <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative flex items-center justify-between">
          <div>
            <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider ring-1 ring-inset ring-white/20">
              <ShieldIcon className="h-3.5 w-3.5" />
              Platform Access
            </div>
            <h1 className="text-2xl font-bold text-white">Platform Users</h1>
            <p className="mt-1 text-indigo-100">
              Manage Super Admin, Admin, and Moderator accounts that operate across all tenants.
            </p>
          </div>
          {isSuperAdmin && (
            <Button
              variant="secondary"
              onClick={() => navigate('/platform-users/new')}
              className="border-white/30 bg-white/20 text-white hover:bg-white/30"
            >
              <PlusIcon className="mr-1.5 h-4 w-4" />
              Add Platform User
            </Button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-3 lg:gap-6">
        <KpiCard
          title="Total Platform Users"
          value={total.toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-brand-500 to-indigo-600"
          iconBg="bg-gradient-to-br from-brand-500 to-indigo-600"
          tintBg="bg-brand-50"
          darkTintBg="dark:bg-brand-900/20"
          icon={<UsersIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="Active Today"
          value={activeTodayDisplay}
          gradient="bg-gradient-to-br from-success-500 to-success-700"
          iconBg="bg-gradient-to-br from-success-500 to-success-600"
          tintBg="bg-success-50"
          darkTintBg="dark:bg-success-900/20"
          icon={<BoltIcon className="h-5 w-5" />}
        />
        <KpiCard
          title="Super Admins"
          value={(superAdminMeta?.meta?.total ?? 0).toLocaleString('en-IN')}
          gradient="bg-gradient-to-br from-danger-500 to-rose-600"
          iconBg="bg-gradient-to-br from-danger-500 to-rose-600"
          tintBg="bg-danger-50"
          darkTintBg="dark:bg-danger-900/20"
          icon={<CrownIcon className="h-5 w-5" />}
        />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-64">
          <Input
            placeholder="Search by name or email..."
            value={searchInput}
            onChange={(v) => handleSearch(v)}
            data-role="platform-user-search"
          />
        </div>
        <div className="w-44">
          <Select
            options={ROLE_OPTIONS}
            value={roleFilter}
            onChange={(v) => setParam('role', typeof v === 'string' ? v : '')}
            placeholder="All Roles"
          />
        </div>
        <div className="w-40">
          <Select
            options={STATUS_OPTIONS}
            value={activeFilter}
            onChange={(v) => setParam('is_active', typeof v === 'string' ? v : '')}
            placeholder="All"
          />
        </div>
        {hasFilters && (
          <button
            type="button"
            onClick={() => setSearchParams(new URLSearchParams())}
            className="ml-1 text-sm text-brand-600 hover:text-brand-700 dark:text-brand-400"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Data Table */}
      <div className="rounded-xl bg-white/80 shadow-md backdrop-blur-sm animate-slide-up dark:bg-neutral-900/80">
        <DataTable<PlatformUserListItem>
          columns={columns}
          data={users}
          loading={isLoading}
          totalCount={total}
          currentPage={page}
          pageSize={20}
          onPageChange={setPage}
          emptyState={{
            title: 'No platform users match filters',
            description: 'Try a different search term, role, or status.',
          }}
        />
      </div>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function PlatformUserListPage() {
  return (
    <PageContainer
      title="Platform Users"
      breadcrumbs={[
        { label: 'Platform', href: '/dashboard' },
        { label: 'Platform Users' },
      ]}
    >
      <ErrorBoundary>
        <PlatformUserListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
